import { useEffect, useState } from "react";

interface Props {
  images: string[];
  alt: string;
  /** Seconds per slide. Default 6. */
  intervalSec?: number;
}

/**
 * Cross-fading hero slider.
 *
 * - Renders all images stacked absolutely; opacity transitions between them.
 * - Falls back to a single static image when fewer than two URLs are given.
 * - Dots at the bottom let visitors jump to a specific slide.
 * - Auto-advance pauses when the tab is hidden (`document.hidden`)
 *   to avoid waste of GPU on background tabs.
 */
export function HeroSlider({ images, alt, intervalSec = 6 }: Props) {
  const valid = images.filter((u) => !!u && u.trim() !== "");
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (valid.length < 2) return;
    let cancelled = false;
    const tick = () => {
      if (cancelled || document.hidden) return;
      setIdx((i) => (i + 1) % valid.length);
    };
    const h = window.setInterval(tick, Math.max(2, intervalSec) * 1000);
    return () => { cancelled = true; window.clearInterval(h); };
  }, [valid.length, intervalSec]);

  if (valid.length === 0) return null;

  return (
    <div className="absolute inset-0">
      {valid.map((src, i) => (
        <img
          key={src + i}
          src={src}
          alt={i === 0 ? alt : ""}
          aria-hidden={i !== idx}
          loading={i === 0 ? "eager" : "lazy"}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ease-in-out"
          style={{ opacity: i === idx ? 1 : 0 }}
        />
      ))}

      {valid.length > 1 && (
        <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-10">
          {valid.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              aria-label={"Bild " + (i + 1) + " von " + valid.length}
              aria-current={i === idx}
              className="w-2.5 h-2.5 rounded-full transition-colors"
              style={{
                background: i === idx ? "var(--color-accent)" : "rgba(255,255,255,0.55)",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
